/**
 * Calculator API service
 * Handles all API calls related to the calculator functionality
 */
import { API_BASE_URL, DEFAULT_FETCH_CONFIG, handleApiError } from './config';

/**
 * Internal worker data with additional fields used by the UI
 */
export interface WorkerDetails {
    type: string;
    number: number;
    title?: string;      // For UI display
    quantity?: number;   // For UI calculations
    hourly_rate?: number; // For UI calculations
}

/**
 * Internal tranche data with additional fields used by the UI
 */
export interface TrancheDetails {
    number: number;
    price: number;
    description?: string; // For UI display
}

/**
 * API request data for worker - only includes fields required by API
 */
export interface EstimateWorker {
    type: string;     // Worker type from predefined list
    number: number;   // Quantity of workers needed for this role
}

/**
 * API request data for tranche - only includes fields required by API
 */
export interface Tranche {
    number: number;   // Unique identifier for the tranche
    price: number;    // Price of the tranche
}

/**
 * Questionnaire data structure for the estimate
 */
export interface Questionnaire {
    challenge: string;
    services: string[];
    company_location: string;
    details: string | null;
}

export interface EstimateData {
    dnd_workshops_costs: number;
    travel_costs: number;
    tranches: Tranche[];
    total_costs: number;
    workers: EstimateWorker[];
    delivery_estimate_days: number;
    milestones: number;
    currency: string;
    sprint_duration: number;
    project_name: string;
    questionnaire: Questionnaire;
}

export interface EstimateRequest {
    email: string;
    estimate: EstimateData;
}

export interface ApiResponse {
    id?: string;
    confirmed?: boolean;
    _fallback?: 'parse_failed' | 'cors_or_network';
    [key: string]: any;
}

/**
 * Worker type enum with valid values for the API
 */
export enum WorkerType {
    PROJECT_MANAGER = 'project_manager',
    UI_DESIGNER = 'ui_designer',
    UX_DESIGNER = 'ux_designer',
    UI_UX_DESIGNER = 'ui_ux_designer',
    GENERIC_DEV = 'generic_dev',
    TESTER = 'tester',
    DEVOPS = 'devops',
    FE_DEV = 'fe_dev',
    BE_DEV = 'be_dev',
    FULL_STACK_DEV = 'full_stack_dev',
    GAME_DEV = 'game_dev',
    AI_DEV = 'ai_dev',
    ML_DEV = 'ml_dev',
    AI_ML_DEV = 'ai_ml_dev',
    DATA_ANALYST = 'data_analyst'
}

/**
 * Map Polish role titles to API worker types
 * @param polishTitle The Polish role title
 * @returns The API worker type
 */
export const mapWorkerTypeToApi = (polishTitle: string): string => {
    // Map our Polish titles to the API's expected values
    const titleMap: Record<string, WorkerType> = {
        'Kierownik Projektu': WorkerType.PROJECT_MANAGER,
        'Projektant UI/UX': WorkerType.UI_UX_DESIGNER,
        'Programista': WorkerType.GENERIC_DEV,
        'Programista BE': WorkerType.BE_DEV,
        'Programista FE': WorkerType.FE_DEV,
        'Specjalista DevOps': WorkerType.DEVOPS,
        'Tester': WorkerType.TESTER,
        'Inżynier ML': WorkerType.ML_DEV,
        'Inżynier AI': WorkerType.AI_DEV,
        'Inżynier AI/ML': WorkerType.AI_ML_DEV,
        'Konsultant': WorkerType.GENERIC_DEV,
        'Analityk danych': WorkerType.DATA_ANALYST,
    };

    // Convert the result to string to ensure compatibility
    return (titleMap[polishTitle] || WorkerType.GENERIC_DEV) as string;
};

/**
 * Format team data for API submission
 * @param teamComposition The team composition data
 * @param teamDetails Team details with hourly rates
 * @returns Formatted workers array for API
 */
export const formatTeamData = (
    teamComposition: Record<string, number>,
    teamDetails: Record<string, { title: string; hourlyRate: number }>
): EstimateWorker[] => {
    return Object.entries(teamComposition)
        .filter(([_, quantity]) => quantity > 0)
        .map(([key, quantity]) => {
            const member = teamDetails[key] || { title: key, hourlyRate: 0 };
            return {
                type: mapWorkerTypeToApi(member.title),
                number: quantity,
            };
        });
};

/**
 * Create tranches array for API submission
 * @param count Number of tranches
 * @param cost Cost per tranche
 * @returns Formatted tranches array for API
 */
export const createTranches = (count: number, cost: number): Tranche[] => {
    return Array(count).fill(null).map((_, index) => ({
        number: index + 1,
        price: cost
    }));
};

/**
 * Debug logger to help with API troubleshooting
 * @param message Message to log
 * @param data Data to log
 */
const debugLog = (message: string, data: any) => {
    if (process.env.NODE_ENV !== 'production') {
        console.log(`[API Debug] ${message}:`, data);
    }
};

/**
 * Validate and sanitize estimate data before sending to API
 * @param data The request data to validate
 * @returns The validated and sanitized data
 */
const validateEstimateData = (data: EstimateRequest): EstimateRequest => {
    // Create a deep clone to avoid modifying the original
    const sanitized = JSON.parse(JSON.stringify(data)) as EstimateRequest;
    
    // Ensure all numeric fields are actually numbers, not strings
    sanitized.estimate.dnd_workshops_costs = Number(sanitized.estimate.dnd_workshops_costs);
    sanitized.estimate.travel_costs = Number(sanitized.estimate.travel_costs);
    sanitized.estimate.total_costs = Number(sanitized.estimate.total_costs);
    sanitized.estimate.delivery_estimate_days = Number(sanitized.estimate.delivery_estimate_days);
    sanitized.estimate.milestones = Number(sanitized.estimate.milestones);
    sanitized.estimate.sprint_duration = Number(sanitized.estimate.sprint_duration);
    
    // Ensure tranches are properly formatted
    sanitized.estimate.tranches = sanitized.estimate.tranches.map((tranche, index) => ({
        number: Number(tranche.number || index + 1),
        price: Number(tranche.price)
    }));
    
    // Ensure workers are properly formatted and have valid types
    sanitized.estimate.workers = sanitized.estimate.workers.map((worker, index) => {
        // If the worker type is a string but not a valid WorkerType value, map it
        let workerType = worker.type;
        if (typeof workerType === 'string' && !Object.values(WorkerType).includes(workerType as WorkerType)) {
            workerType = mapWorkerTypeToApi(workerType);
        }
        
        return {
            type: workerType,
            number: Number(worker.number || index + 1)
        };
    });
    
    return sanitized;
};

/**
 * Submits a calculator estimate to the API
 * @param data The estimate request data
 * @returns The API response
 */
export const submitEstimate = async (data: EstimateRequest): Promise<ApiResponse> => {
    // Validate and sanitize the data
    const validData = validateEstimateData(data);
    
    // Log the request data for debugging
    debugLog('Submitting estimate', validData);
    return submitWithRetry(validData);
};

/**
 * Submits a request with retry functionality for error handling
 * @param submissionData The data to submit
 * @param retries Number of retries on failure
 * @returns The API response
 */
const submitWithRetry = async (submissionData: EstimateRequest, retries = 2): Promise<ApiResponse> => {
    try {
        debugLog('Making API request', {
            url: `${API_BASE_URL}/estimates`,
            method: 'POST',
            data: submissionData
        });

        const response = await fetch(`${API_BASE_URL}/estimates`, {
            ...DEFAULT_FETCH_CONFIG,
            method: 'POST',
            body: JSON.stringify(submissionData)
        });

        debugLog('API response status', {
            status: response.status,
            statusText: response.statusText
        });

        // If we get a 200 status, it's successful even if CORS prevents reading the response
        if (response.status >= 200 && response.status < 300) {
            try {
                // Try to parse the response, but it might fail due to CORS
                const responseData = await response.json();
                debugLog('API response data', responseData);
                return responseData;
            } catch (parseError) {
                // 2xx received but response body unreadable (typically CORS hiding it).
                // Request reached the server; we just can't read the payload.
                debugLog('Could not parse response, but status was successful', {
                    status: response.status,
                    error: parseError
                });
                return {
                    success: true,
                    confirmed: false,
                    _fallback: 'parse_failed',
                    message: 'Request was successful, but response could not be read due to CORS restrictions'
                };
            }
        }

        if (!response.ok) {
            // For 5xx errors, retry if we have retries left
            if (response.status >= 500 && retries > 0) {
                console.log(`Retrying API call, ${retries} left`);
                // Wait for a second before retrying
                await new Promise(resolve => setTimeout(resolve, 1000));
                return submitWithRetry(submissionData, retries - 1);
            }
            
            // Clone the response before consuming it to log it
            const clonedResponse = response.clone();
            clonedResponse.json().then(errorData => {
                debugLog('Error response data', errorData);
            }).catch(() => {
                debugLog('Could not parse error response as JSON', {});
            });
            
            // Otherwise, handle error through common error handler
            return handleApiError(response);
        }
        
        const responseData = await response.json();
        debugLog('API response data', responseData);
        return responseData;
    } catch (error) {
        // If we have a CORS error, but we're confident the API call is succeeding,
        // we can return a success response instead of throwing an error
        if (error instanceof TypeError &&
            error.message.includes('Failed to fetch') &&
            submissionData.email && submissionData.estimate) {

            // Failed-to-fetch covers offline / DNS / TLS / real CORS / blocked / server-down.
            // We can't tell them apart client-side. Treat as optimistic and tag the response
            // so analytics can distinguish this from a confirmed 2xx.
            debugLog('CORS or network error — treating as optimistic (unconfirmed)', error);
            return {
                success: true,
                confirmed: false,
                _fallback: 'cors_or_network',
                message: 'Request was likely successful, but could not be confirmed due to CORS restrictions'
            };
        }
        
        if (retries > 0 && error instanceof Error && error.message.includes('fetch')) {
            console.log(`Network error, retrying, ${retries} left`);
            // Wait for a second before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            return submitWithRetry(submissionData, retries - 1);
        }
        debugLog('API call error', error);
        throw error;
    }
}; 